import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ArrowLeft, Sparkles, Clock, Award, Filter, Search, BookOpen, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_KEYS = ["confiance", "estime_de_soi", "clarté_objectifs", "gestion_emotions", "equilibre_vie", "adaptabilité", "resilience", "bienveillance_envers_soi", "affirmation_de_soi", "croyance_croissance"];

export default function ExercisesPage() {
  const { t, i18n } = useTranslation();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchExercises();
  }, [i18n.language]);

  const fetchExercises = async () => {
    try {
      const response = await axios.get(`${API}/exercises?lang=${i18n.language}`);
      setExercises(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching exercises:", error);
      setLoading(false);
    }
  };

  const filteredExercises = exercises.filter(ex => {
    const matchesCategory = selectedCategory === "all" || ex.category === selectedCategory;
    const matchesSearch = ex.title.toLowerCase().includes(searchQuery.toLowerCase()) || ex.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getDifficultyColor = (d) => d === 'Facile' || d === 'Easy' ? 'bg-[#A8C3A0] text-white' : d === 'Moyen' || d === 'Medium' ? 'bg-[#E2725B] text-white' : 'bg-[#2F4F4F] text-white';
  const getCategoryColor = (cat) => {
    const colors = { confiance: 'bg-blue-100 text-blue-700', estime_de_soi: 'bg-purple-100 text-purple-700', clarté_objectifs: 'bg-amber-100 text-amber-700', gestion_emotions: 'bg-green-100 text-green-700', equilibre_vie: 'bg-cyan-100 text-cyan-700', adaptabilité: 'bg-orange-100 text-orange-700', resilience: 'bg-red-100 text-red-700', bienveillance_envers_soi: 'bg-pink-100 text-pink-700', affirmation_de_soi: 'bg-indigo-100 text-indigo-700', croyance_croissance: 'bg-teal-100 text-teal-700' };
    return colors[cat] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (<div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center"><div className="text-center"><Loader2 className="w-12 h-12 text-[#2F4F4F] animate-spin mx-auto mb-4" /><p className="text-[#5C5C5C]">{t('exercises.loading')}</p></div></div>);
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="fixed top-0 left-0 right-0 z-50 glass-header border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#2F4F4F] hover:opacity-80" data-testid="back-home-link">
            <ArrowLeft className="w-5 h-5" /><span className="font-medium">{t('nav.home')}</span>
          </Link>
          <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#2F4F4F]" /><span className="font-heading text-lg text-[#2F4F4F]">Épanoui</span></div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/diagnostic" data-testid="start-diagnostic-btn"><Button className="rounded-full bg-[#2F4F4F] hover:bg-[#2F4F4F]/90 text-white">{t('nav.diagnostic')}</Button></Link>
          </div>
        </div>
      </header>

      <main className="pt-28 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 text-center">
            <h1 className="font-heading text-4xl font-semibold text-[#2D2D2D] mb-4" data-testid="exercises-title">
              {t('exercises.title')} <em className="text-[#E2725B] not-italic">{t('exercises.highlight')}</em>
            </h1>
            <p className="text-[#5C5C5C] max-w-2xl mx-auto">{t('exercises.description')}</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#8A8A8A]" />
              <Input type="text" placeholder={t('exercises.searchPlaceholder')} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-12 h-12 rounded-xl border-gray-200 bg-white" data-testid="search-input" />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full sm:w-64 h-12 rounded-xl border-gray-200 bg-white" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2 text-[#8A8A8A]" /><SelectValue placeholder={t('exercises.allCategories')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('exercises.allCategories')}</SelectItem>
                {CATEGORY_KEYS.map(key => (<SelectItem key={key} value={key}>{t(`categories.${key}`)}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          <p className="text-sm text-[#8A8A8A] mb-6">{filteredExercises.length} {t('exercises.found')}</p>

          {filteredExercises.length === 0 ? (
            <div className="text-center py-16"><BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-[#5C5C5C]">{t('exercises.noResults')}</p></div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredExercises.map((ex) => (
                <Card key={ex.id} className="bg-white border-gray-100 shadow-lg hover:shadow-xl cursor-pointer" onClick={() => setExpandedId(expandedId === ex.id ? null : ex.id)} data-testid={`exercise-card-${ex.id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(ex.difficulty)}`}><Award className="w-3 h-3 inline mr-1" />{ex.difficulty}</span>
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-[#5C5C5C]"><Clock className="w-3 h-3 inline mr-1" />{ex.duration}</span>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-[#2D2D2D] mb-2">{ex.title}</h3>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(ex.category)} mb-3`}>{t(`categories.${ex.category}`)}</span>
                    <p className="text-[#5C5C5C] text-sm mb-4">{ex.description}</p>
                    {expandedId === ex.id && (
                      <div className="mt-4 pt-4 border-t border-gray-100 animate-fade-in">
                        <h4 className="font-medium text-[#2D2D2D] mb-3">{t('exercises.steps')}</h4>
                        <ol className="space-y-3">
                          {ex.steps.map((step, i) => (<li key={i} className="flex items-start gap-3"><span className="w-6 h-6 rounded-full bg-[#A8C3A0]/20 text-[#2F4F4F] text-sm font-medium flex items-center justify-center flex-shrink-0">{i + 1}</span><span className="text-sm text-[#5C5C5C] leading-relaxed">{step}</span></li>))}
                        </ol>
                      </div>
                    )}
                    <p className="text-xs text-[#8A8A8A] mt-4">{expandedId === ex.id ? t('exercises.clickToCollapse') : t('exercises.clickToExpand')}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="mt-16 text-center">
            <Card className="bg-white border-gray-100 shadow-lg inline-block">
              <CardContent className="p-8">
                <h2 className="font-heading text-2xl font-semibold text-[#2D2D2D] mb-4">{t('exercises.ctaTitle')}</h2>
                <p className="text-[#5C5C5C] mb-6 max-w-md">{t('exercises.ctaDescription')}</p>
                <Link to="/diagnostic" data-testid="cta-diagnostic-btn"><Button className="rounded-full bg-[#E2725B] hover:bg-[#E2725B]/90 text-white px-8 py-6 text-lg">{t('nav.diagnostic')}<Sparkles className="w-5 h-5 ml-2" /></Button></Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
